/**
 * ScenePhotoView — the single immersive "photo + bbox overlays + item
 * carousel" surface (#169 C2 + #170 ShareSheet).
 *
 * ONE scene-photo page, same immersive layout as the post-scan review
 * (full-bleed photo with bbox overlays + a bottom item carousel +
 * scroll-down "All items" list), composing the SAME leaf components the
 * scan screen uses (ImageAnnotationStage, FocusedItemCarousel, ItemRail)
 * in read-only mode.
 *
 * Sharing (#170): the cards are now FULLY read-only — selection no longer
 * lives on the cards. A single "Share" button opens the unified
 * `ShareSheet`, pre-filled with the scene's current share-state (per
 * group, per item) so the user adds/removes inside one modal. Present
 * share-state stays visible read-only: the "Shared with: …" header banner
 * + each card's "Shared · {group}" badge (both derived from item.groups).
 */

import React, { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Dimensions,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import Toast from 'react-native-toast-message';

import { fpColors } from '../../../styles/theme';
import { useAppDispatch } from '../../../store/store';
import {
  ImageAnnotationStage,
  type AnnotationItem,
} from '../../segment/components/ImageAnnotationStage';
import { FocusedItemCarousel } from '../../segment/components/FocusedItemCarousel';
import { ItemRail, type ItemRailItem } from '../../segment/components/ItemRail';
import { ShareSheet, type SharePlan } from '../../sharing/components/ShareSheet';
import { commitSharePlanToServer } from '../../sharing/commitSharePlan';
import { ImageInfoSheet } from '../../segment/components/ImageInfoSheet';
import { ImageEnlargementModal } from '../../../components/ImageEnlargementModal';
import { deleteInventoryItem } from '../browseSlice';
import type { InventoryItemResponse } from '../../../api/types';
import { DrawNewItemOverlay } from '../../segment/components/DrawNewItemOverlay';
import {
  TapAddGhost,
  type TapAddRecord,
} from '../../segment/components/TapAddGhost';
import { manufactureSquareBbox } from '../../segment/manufactureSquareBbox';
import type { Bbox } from '../../segment/useImageTransform';
import type { ManualAnalyzeResponse } from '../../../api/scanStreamTypes';

export interface ScenePhotoViewProps {
  mode?: 'saved-scene' | 'scan-review';
  imageUrl: string | null;
  items: InventoryItemResponse[];
  title: string;
  subtitle?: string;
  aiDescription?: string | null;
  onBack: () => void;
  onItemPress?: (item: InventoryItemResponse) => void;
  /** Fired after a successful share commit so the host can refetch the
   *  scene (updates the "Shared with" banner + per-card badges). */
  onSharesChanged?: () => void;
  /** #170 #4 — "＋ Add" on the scene. Legacy behavior: host navigates
   *  somewhere else. NEW (BE #411 tap-add) behavior: when `tapAddAnalyze`
   *  is also provided, `+` toggles in-place tap-add mode instead.
   *  Hidden entirely when both are omitted. */
  onAddItem?: () => void;
  /** AI tap-add (BE #411). When set, `+` toggles a draw/tap overlay over
   *  the photo. A single tap or a drag fires this callback with the
   *  image-pixel bbox; the host POSTs `/inventory/manual-analyze` and
   *  returns the response. The component handles ghost rendering +
   *  state morphs (pending → success / softfail / harderror) itself. */
  tapAddAnalyze?: (
    bbox: Bbox,
    meta: { draw_duration_ms: number; device_kind: 'mouse' | 'touch' | 'pen' },
  ) => Promise<ManualAnalyzeResponse>;
  /** Fires after a successful (or soft-fail) tap-add round so the host
   *  can refetch the scene to surface the new item in the rail. */
  onItemAdded?: (itemId: number, isSoftFail: boolean) => void;
  /** Delete the whole scan / source file_record. Host owns the confirm
   *  flow (DeleteSceneSheet) + navigation away. Renders a trash button
   *  in the top-right HUD when provided. */
  onDelete?: () => void;
}

const NOOP = () => {};
const idOf = (it: InventoryItemResponse): number | null =>
  typeof (it as any)?.id === 'number' ? (it as any).id : null;

export const ScenePhotoView: React.FC<ScenePhotoViewProps> = ({
  mode = 'saved-scene',
  imageUrl,
  items,
  title,
  subtitle,
  aiDescription,
  onBack,
  onItemPress,
  onSharesChanged,
  onAddItem,
  tapAddAnalyze,
  onItemAdded,
  onDelete,
}) => {
  const dispatch = useAppDispatch();
  const [focusedItemId, setFocusedItemId] = useState<number | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [applying, setApplying] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [zoomOpen, setZoomOpen] = useState(false);
  // Source-view story — let the user hide the AI detection overlays to
  // see the clean photo (boxes are back on with one more tap).
  const [boxesHidden, setBoxesHidden] = useState(false);
  // AI tap-add (BE #411). Mode toggle + ghosts list. When
  // `tapAddAnalyze` is provided, tapping `+` flips this on and the
  // image gets a draw/tap capture surface.
  const [tapAddMode, setTapAddMode] = useState(false);
  const [tapAddInflight, setTapAddInflight] = useState<TapAddRecord[]>([]);

  const dismissGhost = useCallback((id: string) => {
    setTapAddInflight((prev) => prev.filter((r) => r.id !== id));
  }, []);

  const runAnalyze = useCallback(
    async (
      bbox: Bbox,
      meta: { draw_duration_ms: number; device_kind: 'mouse' | 'touch' | 'pen' },
    ) => {
      if (!tapAddAnalyze) return;
      const id = `tap-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
      const startedAt = performance.now();
      const bboxInt = {
        x: Math.round(bbox.x),
        y: Math.round(bbox.y),
        w: Math.round(bbox.w),
        h: Math.round(bbox.h),
      };
      setTapAddInflight((prev) => [
        ...prev,
        { id, bbox: bboxInt, startedAt, status: 'pending' },
      ]);
      // Exit tap mode synchronously so a second tap is a fresh round
      // rather than another capture on the same gesture.
      setTapAddMode(false);
      try {
        const res = await tapAddAnalyze(bboxInt, meta);
        const isSoftFail =
          typeof res.error === 'string' && res.error.length > 0;
        setTapAddInflight((prev) =>
          prev.map((r) =>
            r.id === id
              ? {
                  ...r,
                  status: isSoftFail ? 'softfail' : 'success',
                  resolvedAt: performance.now(),
                  latencyMs: res.latency_ms,
                  itemName: res.item?.item_name,
                  errorMessage: isSoftFail
                    ? res.error ?? undefined
                    : undefined,
                }
              : r,
          ),
        );
        const itemId = (res.item as any)?.id;
        if (typeof itemId === 'number' && onItemAdded) {
          onItemAdded(itemId, isSoftFail);
        }
      } catch (err: any) {
        const status = err?.response?.status;
        let friendly =
          err?.response?.data?.detail || err?.message || 'Could not add item';
        if (status === 429)
          friendly = 'Slow down — limit hit. Try again in a minute.';
        else if (status === 403) friendly = 'Can’t access this photo.';
        else if (status === 404) friendly = 'This photo was deleted.';
        else if (status === 422) friendly = 'Bbox math is off — try again.';
        setTapAddInflight((prev) =>
          prev.map((r) =>
            r.id === id
              ? {
                  ...r,
                  status: 'harderror',
                  resolvedAt: performance.now(),
                  errorMessage: friendly,
                }
              : r,
          ),
        );
      }
    },
    [tapAddAnalyze, onItemAdded],
  );

  const handleAddPress = useCallback(() => {
    // Prefer the new tap-add path when wired; fall back to the legacy
    // host-driven `onAddItem` (used by surfaces that still navigate).
    if (tapAddAnalyze) {
      setTapAddMode((v) => !v);
      return;
    }
    onAddItem?.();
  }, [tapAddAnalyze, onAddItem]);

  const showPlusButton = !!(onAddItem || tapAddAnalyze);

  const win = Dimensions.get('window');

  // ── present share-state (read-only) + ShareSheet pre-fill ─────────
  const groupsOf = useCallback(
    (it: InventoryItemResponse | undefined) =>
      (((it as any)?.groups as
        | Array<{ id: number; name: string; kind?: string }>
        | undefined) ?? []).filter((g) => g && g.kind !== 'PERSONAL'),
    [],
  );

  const sharedWithNames = useMemo(() => {
    const names = new Set<string>();
    for (const it of items) for (const g of groupsOf(it)) if (g.name) names.add(g.name);
    return Array.from(names);
  }, [items, groupsOf]);

  // groupId → itemIds currently shared (feeds ShareSheet.initialShares)
  const initialShares = useMemo<Record<number, number[]>>(() => {
    const m: Record<number, number[]> = {};
    for (const it of items) {
      const id = idOf(it);
      if (id == null) continue;
      for (const g of groupsOf(it)) {
        (m[g.id] = m[g.id] ?? []).push(id);
      }
    }
    return m;
  }, [items, groupsOf]);

  const handleApply = useCallback(
    async (plan: SharePlan) => {
      setApplying(true);
      try {
        const res = await commitSharePlanToServer(dispatch, plan, initialShares);
        const parts: string[] = [];
        if (res.added) parts.push(`${res.added} shared`);
        if (res.removed) parts.push(`${res.removed} unshared`);
        if (res.groupsCreated)
          parts.push(
            `${res.groupsCreated} new circle${res.groupsCreated === 1 ? '' : 's'}`,
          );
        Toast.show({
          type: res.failures.length ? 'info' : 'success',
          text1: parts.length ? parts.join(' · ') : 'No changes',
          text2: res.failures[0],
        });
        setShareOpen(false);
        onSharesChanged?.();
      } finally {
        setApplying(false);
      }
    },
    [dispatch, initialShares, onSharesChanged],
  );

  // ── stage + rail data (cards are read-only; no share-selection) ───
  const annotationItems = useMemo<AnnotationItem[]>(() => {
    const out: AnnotationItem[] = [];
    for (const it of items) {
      const id = (it as any).id;
      if (typeof id !== 'number') continue;
      const bbox = (it as any).bbox;
      if (!bbox) continue;
      out.push({
        id,
        bbox,
        mask_url: (it as any).mask_url ?? null,
        model: (it as any).segmentation_model ?? 'sam3',
        selected: false,
        name: it.item_name,
      });
    }
    return out;
  }, [items]);

  const railItems = useMemo<ItemRailItem[]>(
    () =>
      items.map((it) => {
        const id = (it as any)?.id ?? -1;
        return {
          item: it,
          selected: false,
          hasMask: !!(it as any)?.mask_url,
          highlighted: focusedItemId === id,
        };
      }),
    [items, focusedItemId],
  );

  const handleFocusBbox = useCallback((id: number) => {
    setFocusedItemId((prev) => (prev === id ? null : id));
  }, []);

  // #170 #7 — delete an item from the scene (full inventory delete, with
  // confirm). Drives the 🗑 on the focused-item action strip.
  const handleDelete = useCallback(
    (id: number) => {
      if (typeof id !== 'number' || id <= 0) return;
      const proceed = () => {
        dispatch(deleteInventoryItem(String(id)) as any);
        setFocusedItemId(null);
        Toast.show({ type: 'success', text1: 'Item deleted' });
        onSharesChanged?.();
      };
      if (typeof window !== 'undefined' && typeof window.confirm === 'function') {
        if (window.confirm('Delete this item? It will be removed from your inventory.')) {
          proceed();
        }
      } else {
        Alert.alert(
          'Delete item?',
          'It will be removed from your inventory.',
          [
            { text: 'Cancel', style: 'cancel' },
            { text: 'Delete', style: 'destructive', onPress: proceed },
          ],
        );
      }
    },
    [dispatch, onSharesChanged],
  );

  const itemCount = items.length;

  return (
    <View style={styles.page} testID={`scene-photo-view-${mode}`}>
      <ScrollView style={styles.pageScroll} showsVerticalScrollIndicator>
        {/* Image-fit sweep (2026-06-23): the "first screen" used to
            be `win.height` (full viewport), which letterboxed the
            common landscape scan photo with huge dead strips when
            ImageAnnotationStage applied object-fit: contain. Cap at
            4:3 of the viewport width (or 70% of viewport height,
            whichever is smaller) so the photo dominates the frame
            without forcing the user to scroll just to see the
            action bar. */}
        <View style={[styles.firstScreen, {
          height: Math.min(win.width * (4 / 3), win.height * 0.7),
        }]}>
          <View style={styles.stageFullscreen}>
            {imageUrl ? (
              <ImageAnnotationStage
                imageUrl={imageUrl}
                items={annotationItems}
                onToggleSelected={NOOP}
                onFocusBbox={handleFocusBbox}
                onRefineItem={undefined}
                onDeleteItem={handleDelete}
                keepUnfocusedVisible
                focusedItemId={focusedItemId}
                editingItemId={null}
                hideAnnotations={boxesHidden}
                fillParent
              >
                {/* AI tap-add overlay + ghosts — only mounted when the
                    host wires `tapAddAnalyze`. The render-prop gives us
                    the stage's transform + container ref so coords map
                    correctly to natural image pixels. */}
                {tapAddAnalyze
                  ? ({ transform, containerRef }: any) => (
                      <>
                        <DrawNewItemOverlay
                          active={tapAddMode}
                          transform={transform}
                          containerRef={containerRef}
                          onCommit={runAnalyze}
                          onTap={(anchor, meta) => {
                            const sz = transform.imageSize;
                            if (!sz) return;
                            const bbox = manufactureSquareBbox(
                              anchor.x,
                              anchor.y,
                              sz.width,
                              sz.height,
                            );
                            runAnalyze(bbox, meta);
                          }}
                          onTapMiss={() => {
                            Toast.show({
                              type: 'info',
                              text1: 'Tap on the photo',
                              text2:
                                'That tap missed the image — aim inside the frame.',
                            });
                          }}
                        />
                        {tapAddInflight.map((rec) => (
                          <TapAddGhost
                            key={rec.id}
                            record={rec}
                            transform={transform}
                            onDismiss={dismissGhost}
                          />
                        ))}
                      </>
                    )
                  : null}
              </ImageAnnotationStage>
            ) : (
              <View style={styles.stagePlaceholder}>
                <MaterialIcons name="image" size={32} color={fpColors.outline} />
                <Text style={styles.stagePlaceholderText}>
                  No source photo available
                </Text>
              </View>
            )}
          </View>

          {/* HUD top — back + title + share */}
          <View style={styles.hudTop} pointerEvents="box-none">
            <Pressable
              onPress={onBack}
              accessibilityRole="button"
              accessibilityLabel="Back"
              hitSlop={10}
              style={styles.hudBtn}
              testID="scene-photo-back"
            >
              <MaterialIcons name="arrow-back" size={20} color={fpColors.onSurface} />
            </Pressable>
            <View style={styles.hudTitleWrap} pointerEvents="none">
              <Text style={styles.hudTitle} numberOfLines={1}>
                {title}
              </Text>
              {subtitle ? (
                <Text style={styles.hudSubtitle} numberOfLines={1}>
                  {subtitle}
                </Text>
              ) : null}
            </View>
            {/* #170 #4 — Share moved to the bottom action bar. Top-right
                now hosts the delete-scan affordance (when the host wires
                it); otherwise a spacer keeps the title centered. */}
            {onDelete ? (
              <Pressable
                onPress={onDelete}
                accessibilityRole="button"
                accessibilityLabel="Delete this scan"
                hitSlop={10}
                style={styles.hudBtn}
                testID="scene-delete-btn"
              >
                <MaterialIcons
                  name="delete-outline"
                  size={20}
                  color={fpColors.error}
                />
              </Pressable>
            ) : (
              <View style={styles.hudBtn} />
            )}
          </View>

          {/* Shared-with banner (read-only present state) */}
          {sharedWithNames.length > 0 ? (
            <View style={styles.sharedWithBanner} testID="scene-shared-with">
              <MaterialIcons name="groups" size={13} color={fpColors.primary} />
              <Text style={styles.sharedWithText} numberOfLines={1}>
                Shared with: {sharedWithNames.join(' · ')}
              </Text>
            </View>
          ) : null}

          {/* #170 #4 — Bottom action bar [Info · ＋ Add · Share], consistent
              with the scan screen (minus Save). Then the read-only carousel. */}
          <View style={styles.dock} pointerEvents="box-none">
            <View style={styles.dockCtaRow}>
              <Pressable
                onPress={() => setInfoOpen(true)}
                disabled={!aiDescription}
                style={({ pressed }) => [
                  styles.sceneActionBtn,
                  !aiDescription && { opacity: 0.4 },
                  pressed && aiDescription && { opacity: 0.85 },
                ]}
                accessibilityRole="button"
                accessibilityLabel="Snapshot info"
                testID="scene-info-btn"
              >
                <MaterialIcons name="info-outline" size={18} color={fpColors.onSurface} />
              </Pressable>
              {imageUrl ? (
                <Pressable
                  onPress={() => setZoomOpen(true)}
                  style={({ pressed }) => [
                    styles.sceneActionBtn,
                    pressed && { opacity: 0.85 },
                  ]}
                  accessibilityRole="button"
                  accessibilityLabel="View the full photo"
                  testID="scene-zoom-btn"
                >
                  <MaterialIcons name="fullscreen" size={20} color={fpColors.onSurface} />
                </Pressable>
              ) : null}
              {imageUrl && itemCount > 0 ? (
                <Pressable
                  onPress={() => setBoxesHidden((v) => !v)}
                  style={({ pressed }) => [
                    styles.sceneActionBtn,
                    pressed && { opacity: 0.85 },
                  ]}
                  accessibilityRole="button"
                  accessibilityState={{ selected: boxesHidden }}
                  accessibilityLabel={
                    boxesHidden ? 'Show detection boxes' : 'Hide detection boxes'
                  }
                  testID="scene-toggle-boxes"
                >
                  <MaterialIcons
                    name={boxesHidden ? 'layers-clear' : 'layers'}
                    size={20}
                    color={boxesHidden ? fpColors.primary : fpColors.onSurface}
                  />
                </Pressable>
              ) : null}
              {showPlusButton ? (
                <Pressable
                  onPress={handleAddPress}
                  style={({ pressed }) => [
                    styles.sceneActionBtn,
                    pressed && { opacity: 0.85 },
                    tapAddMode && styles.sceneActionBtnActive,
                  ]}
                  accessibilityRole="button"
                  accessibilityState={{ selected: tapAddMode }}
                  accessibilityLabel={
                    tapAddMode
                      ? 'Cancel adding an item'
                      : 'Add an item to this snapshot'
                  }
                  testID="scene-add-item-btn"
                >
                  <MaterialIcons
                    name={tapAddMode ? 'close' : 'add'}
                    size={20}
                    color={
                      tapAddMode ? fpColors.onPrimary : fpColors.primary
                    }
                  />
                </Pressable>
              ) : null}
              <Pressable
                onPress={() => itemCount > 0 && setShareOpen(true)}
                disabled={itemCount === 0}
                style={({ pressed }) => [
                  styles.shareBtn,
                  itemCount === 0 && { opacity: 0.5 },
                  pressed && itemCount > 0 && { opacity: 0.88 },
                ]}
                accessibilityRole="button"
                accessibilityLabel="Share"
                testID="scene-share-btn"
              >
                <MaterialIcons name="group-add" size={15} color={fpColors.onPrimary} />
                <Text style={styles.shareBtnText}>Share</Text>
              </Pressable>
            </View>
            <FocusedItemCarousel
              items={railItems}
              focusedItemId={focusedItemId}
              pageWidth={win.width}
              streaming={false}
              readOnly
              onFocusChange={setFocusedItemId}
              onToggleSelected={NOOP}
              onEditDetails={NOOP}
              onRefineBbox={NOOP}
              onDeleteItem={NOOP}
              onItemPress={onItemPress}
            />
          </View>
        </View>

        {/* Second screen — full "All items" list (read-only) */}
        <View style={styles.secondScreen}>
          <Text style={styles.secondScreenHeading}>All items ({items.length})</Text>
          <ItemRail
            items={railItems}
            streaming={false}
            readOnly
            focusedItemId={focusedItemId}
            onToggleSelected={NOOP}
            onEditDetails={NOOP}
            onRefineBbox={NOOP}
            onDeleteItem={NOOP}
            onItemPress={onItemPress}
          />
        </View>
      </ScrollView>

      {shareOpen ? (
        <ShareSheet
          visible
          onClose={() => setShareOpen(false)}
          mode="scene"
          items={items}
          initialShares={initialShares}
          sourceImageUrl={imageUrl}
          onApply={handleApply}
          applying={applying}
        />
      ) : null}

      {/* #170 #6 — scene description behind the Info button (was a bottom
          "About this scene" block). roomContext n/a for saved scenes. */}
      <ImageInfoSheet
        visible={infoOpen}
        roomContext={null}
        aiDescription={aiDescription}
        onClose={() => setInfoOpen(false)}
      />

      {/* #170-followup — "View" the clean scene photo (no bbox overlays),
          pinch / scroll / double-tap to zoom. Reuses #168's lightbox. */}
      {imageUrl ? (
        <ImageEnlargementModal
          visible={zoomOpen}
          imageUri={imageUrl}
          title={title}
          onClose={() => setZoomOpen(false)}
        />
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  page: { flex: 1, backgroundColor: fpColors.background },
  pageScroll: { flex: 1, backgroundColor: fpColors.background },
  firstScreen: { position: 'relative', width: '100%', backgroundColor: '#000' },
  stageFullscreen: {
    position: 'absolute' as any,
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    backgroundColor: '#000',
  },
  stagePlaceholder: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  stagePlaceholderText: { fontSize: 12, color: fpColors.outlineVariant },
  hudTop: {
    position: 'absolute' as any,
    top: 0,
    left: 0,
    right: 0,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingTop: 12,
    paddingBottom: 10,
    backgroundColor: 'rgba(255,255,255,0.85)',
    zIndex: 10,
    ...(typeof window !== 'undefined'
      ? ({ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' } as any)
      : null),
  },
  hudBtn: { width: 32, height: 32, alignItems: 'center', justifyContent: 'center' },
  hudTitleWrap: { flex: 1, paddingHorizontal: 4 },
  hudTitle: { fontSize: 15, fontWeight: '700', color: fpColors.onSurface },
  hudSubtitle: { fontSize: 11, color: fpColors.onSurfaceVariant, marginTop: 1 },
  sharedWithBanner: {
    position: 'absolute' as any,
    top: 58,
    left: 12,
    right: 12,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 10,
    backgroundColor: fpColors.primaryContainer,
    zIndex: 9,
  },
  sharedWithText: {
    flex: 1,
    fontFamily: 'Inter_600SemiBold',
    fontSize: 12,
    color: fpColors.onPrimaryContainer,
  },
  dock: { position: 'absolute' as any, left: 0, right: 0, bottom: 16, zIndex: 9 },
  dockCtaRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 10,
    marginBottom: 8,
  },
  // #170 #4 — Info / + circular buttons in the scene action bar
  // (match the scan toolbar's secondary buttons).
  sceneActionBtn: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: 'rgba(255,255,255,0.92)',
    alignItems: 'center',
    justifyContent: 'center',
    ...(Platform.OS === 'web'
      ? ({ boxShadow: '0 2px 8px rgba(0,0,0,0.18)' } as any)
      : null),
  },
  sceneActionBtnActive: {
    // Active tap-add mode — flip the + button to a solid primary
    // so it reads as a mode toggle, not a single-shot action.
    backgroundColor: fpColors.primary,
  },
  shareBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    height: 38,
    paddingHorizontal: 18,
    borderRadius: 19,
    backgroundColor: fpColors.primary,
    ...(Platform.OS === 'web'
      ? ({ boxShadow: `0 6px 18px ${fpColors.primary}55` } as any)
      : null),
  },
  shareBtnText: { fontFamily: 'Inter_700Bold', fontSize: 13, color: fpColors.onPrimary },
  secondScreen: { paddingTop: 12, paddingBottom: 40, backgroundColor: fpColors.background },
  secondScreenHeading: {
    fontFamily: 'Inter_700Bold',
    fontSize: 13,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    color: fpColors.onSurfaceVariant,
    paddingHorizontal: 16,
    paddingVertical: 10,
  },
  aiDescWrap: {
    marginHorizontal: 16,
    marginTop: 8,
    padding: 12,
    borderRadius: 8,
    backgroundColor: fpColors.surfaceContainerLow,
  },
  aiDescLabel: {
    fontSize: 11,
    fontWeight: '700',
    color: fpColors.onSurfaceVariant,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    marginBottom: 4,
  },
  aiDescText: { fontSize: 13, color: fpColors.onSurface, lineHeight: 18 },
});

export default ScenePhotoView;
